import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

function SponsorManager({ onUpdate }) {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [newLink, setNewLink] = useState('');
  const [newLogoUrl, setNewLogoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingLink, setEditingLink] = useState({});
  const fileInputRef = useRef(null);

  const loadSponsors = async () => {
    try {
      const response = await axios.get('/api/sponsors-admin');
      setSponsors(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Sponsoren');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSponsors(); }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Ungültiges Format. Erlaubt: JPG, PNG, WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Datei zu groß (max. 5MB)');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/api/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewLogoUrl(response.data.url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Hochladen');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } finally {
      setUploading(false);
    }
  };

  const handleAddSponsor = async () => {
    if (!newLogoUrl) {
      setError('Bitte zuerst ein Logo hochladen');
      return;
    }
    setAdding(true);
    setError('');
    try {
      const maxOrder = sponsors.reduce((max, s) => Math.max(max, s.display_order), -1);
      await axios.post('/api/sponsors', {
        name: newName.trim() || null,
        logo_url: newLogoUrl,
        link: newLink.trim() || null,
        display_order: maxOrder + 1
      });
      setNewName('');
      setNewLink('');
      setNewLogoUrl('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadSponsors();
      if (onUpdate) onUpdate();
    } catch (err) {
      setError('Fehler beim Hinzufügen des Sponsors');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/sponsors/${id}`);
      await loadSponsors();
      if (onUpdate) onUpdate();
    } catch {
      setError('Fehler beim Löschen');
    }
  };

  const handleToggleActive = async (sponsor) => {
    try {
      await axios.put(`/api/sponsors/${sponsor.id}`, { is_active: !sponsor.is_active });
      await loadSponsors();
      if (onUpdate) onUpdate();
    } catch {
      setError('Fehler beim Aktualisieren');
    }
  };

  const handleSaveLink = async (sponsor) => {
    const link = editingLink[sponsor.id] ?? sponsor.link ?? '';
    try {
      await axios.put(`/api/sponsors/${sponsor.id}`, { link: link.trim() || null });
      await loadSponsors();
      if (onUpdate) onUpdate();
    } catch {
      setError('Fehler beim Speichern des Links');
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const a = sponsors[index];
    const b = sponsors[index - 1];
    try {
      await Promise.all([
        axios.put(`/api/sponsors/${a.id}`, { display_order: b.display_order }),
        axios.put(`/api/sponsors/${b.id}`, { display_order: a.display_order })
      ]);
      await loadSponsors();
    } catch {
      setError('Fehler beim Sortieren');
    }
  };

  const handleMoveDown = async (index) => {
    if (index === sponsors.length - 1) return;
    const a = sponsors[index];
    const b = sponsors[index + 1];
    try {
      await Promise.all([
        axios.put(`/api/sponsors/${a.id}`, { display_order: b.display_order }),
        axios.put(`/api/sponsors/${b.id}`, { display_order: a.display_order })
      ]);
      await loadSponsors();
    } catch {
      setError('Fehler beim Sortieren');
    }
  };

  if (loading) {
    return <p className="text-slate-500 dark:text-slate-400 text-sm">Lade Sponsoren...</p>;
  }

  return (
    <div>
      {error && (
        <div className="mb-3 px-3 py-2 bg-red-500/10 border border-red-500 rounded-md text-red-500 text-sm">
          {error}
        </div>
      )}

      {/* Bestehende Sponsoren */}
      {sponsors.length === 0 ? (
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">Noch keine Sponsoren hinzugefügt.</p>
      ) : (
        <div className="flex flex-col gap-3 mb-6">
          {sponsors.map((sponsor, index) => {
            const currentLink = editingLink[sponsor.id] !== undefined ? editingLink[sponsor.id] : (sponsor.link || '');
            return (
              <div
                key={sponsor.id}
                className={`flex flex-wrap items-center gap-3 p-3 rounded-lg border transition-colors ${
                  sponsor.is_active
                    ? 'bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600'
                    : 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-700 opacity-60'
                }`}
              >
                {/* Logo Vorschau */}
                <div className="w-20 h-12 flex items-center justify-center bg-slate-100 dark:bg-slate-600 rounded border border-slate-200 dark:border-slate-500 flex-shrink-0">
                  <img
                    src={sponsor.logo_url}
                    alt={sponsor.name || 'Sponsor'}
                    className="max-h-10 max-w-[72px] object-contain"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>

                {/* Name + Link */}
                <div className="flex-1 min-w-[180px] flex flex-col gap-1">
                  {sponsor.name && (
                    <span className="text-sm font-semibold text-slate-800 dark:text-white">{sponsor.name}</span>
                  )}
                  <div className="flex gap-2 items-center">
                    <input
                      type="url"
                      placeholder="https://... (optional)"
                      value={currentLink}
                      onChange={(e) => setEditingLink(prev => ({ ...prev, [sponsor.id]: e.target.value }))}
                      className="flex-1 px-2 py-1 text-sm rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-gold"
                    />
                    <button
                      onClick={() => handleSaveLink(sponsor)}
                      className="px-2 py-1 text-xs rounded bg-gold text-slate-900 font-semibold hover:bg-gold-dark transition-colors flex-shrink-0"
                    >
                      Speichern
                    </button>
                  </div>
                </div>

                {/* Aktiv-Toggle */}
                <label className="relative inline-block w-10 h-6 cursor-pointer flex-shrink-0" title={sponsor.is_active ? 'Aktiv' : 'Inaktiv'}>
                  <input
                    type="checkbox"
                    checked={sponsor.is_active}
                    onChange={() => handleToggleActive(sponsor)}
                    className="sr-only peer"
                  />
                  <span className="absolute inset-0 bg-slate-300 dark:bg-slate-600 rounded-full transition-colors peer-checked:bg-green-500"></span>
                  <span className="absolute left-[3px] bottom-[3px] w-[18px] h-[18px] bg-white rounded-full transition-transform peer-checked:translate-x-4"></span>
                </label>

                {/* Reihenfolge */}
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className="w-7 h-7 rounded bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm hover:bg-slate-300 dark:hover:bg-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Nach oben"
                  >↑</button>
                  <button
                    onClick={() => handleMoveDown(index)}
                    disabled={index === sponsors.length - 1}
                    className="w-7 h-7 rounded bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm hover:bg-slate-300 dark:hover:bg-slate-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Nach unten"
                  >↓</button>
                </div>

                {/* Löschen */}
                <button
                  onClick={() => handleDelete(sponsor.id)}
                  className="w-7 h-7 rounded bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors flex-shrink-0"
                  title="Sponsor löschen"
                >×</button>
              </div>
            );
          })}
        </div>
      )}

      {/* Neuer Sponsor */}
      <div className="p-4 bg-slate-50 dark:bg-slate-700/50 border border-dashed border-slate-300 dark:border-slate-600 rounded-lg">
        <h4 className="font-semibold text-sm text-slate-800 dark:text-white mb-3">Neuen Sponsor hinzufügen</h4>

        <div className="flex flex-col gap-3">
          {/* Logo Upload */}
          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Logo *</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleFileChange}
              disabled={uploading}
              className="text-sm text-slate-700 dark:text-slate-300 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-gold file:text-slate-900 hover:file:bg-gold-dark file:cursor-pointer disabled:opacity-60"
            />
            {uploading && <span className="text-xs text-slate-500 ml-2">Wird hochgeladen...</span>}
            {newLogoUrl && (
              <div className="mt-2 inline-flex items-center gap-2">
                <img src={newLogoUrl} alt="Vorschau" className="h-10 max-w-[120px] object-contain rounded border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 p-1" />
                <button onClick={() => { setNewLogoUrl(''); if (fileInputRef.current) fileInputRef.current.value = ''; }} className="text-xs text-red-500 hover:text-red-700">Entfernen</button>
              </div>
            )}
          </div>

          {/* Name (optional) */}
          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Name (optional)</label>
            <input
              type="text"
              placeholder="z.B. Hauptsponsor GmbH"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full max-w-sm px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-gold"
            />
          </div>

          {/* Link (optional) */}
          <div>
            <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">Website-Link (optional)</label>
            <input
              type="url"
              placeholder="https://www.beispiel.de"
              value={newLink}
              onChange={(e) => setNewLink(e.target.value)}
              className="w-full max-w-sm px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-gold"
            />
          </div>

          <button
            onClick={handleAddSponsor}
            disabled={!newLogoUrl || adding}
            className="self-start px-4 py-2 rounded-lg text-sm font-semibold bg-gold text-slate-900 hover:bg-gold-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {adding ? 'Wird hinzugefügt...' : '+ Sponsor hinzufügen'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SponsorManager;
